import { fulfillmentFilters } from "../../shared/utils/fulfillmentFilters";
import {
    AccessTime,
    CheckCircle,
    NotificationsActive,
    Refresh,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Stack,
    Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppContext";
import HelmetMeta from "../../components/HelmetMeta";
import LanguageToggle from "../../components/LanguageToggle/LanguageToggle";
import LogoutButton from "../../components/LogoutButton";
import { useGetDocTypeListWithChildren } from "../../shared/hooks/useDocTypesData";
import {
    getCustomerOrderNumber,
    getExpectedReadyAt,
    isOrderDelivered,
    isOrderReady,
} from "./orderStatus";

import { createOrderChime, orderSnapshot, hasOrderNotification } from "./orderNotifications";

export const OrderStatusCard = ({ order, ready, now, onDelivered, isDelivering, t }) => {
    const expectedAt = getExpectedReadyAt(order);
    const remainingMinutes = expectedAt
        ? Math.max(0, Math.ceil((expectedAt.getTime() - now) / 60000))
        : null;
    const expectedLabel = expectedAt
        ? new Intl.DateTimeFormat(undefined, {
              hour: "2-digit",
              minute: "2-digit",
          }).format(expectedAt)
        : "—";

    return (
        <Card
            data-testid="customer-order-card"
            elevation={0}
            sx={{
                height: "100%",
                color: ready ? "#0f5132" : "#664d03",
                bgcolor: ready ? "#d1e7dd" : "#fff3cd",
                border: "2px solid",
                borderColor: ready ? "#198754" : "#ffc107",
                borderRadius: 3,
                transition: "background-color 300ms ease, border-color 300ms ease",
            }}
        >
            <CardContent sx={{ p: 1, "&:last-child": { pb: 1 }, height: "100%" }}>
                <Stack spacing={0.75} sx={{ height: "100%" }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="overline" sx={{ fontWeight: 900 }}>
                                {t("kioskPreview.orderNumber")}
                            </Typography>
                            <Typography
                                variant="h3"
                                sx={{ fontWeight: 950, fontSize: 42, lineHeight: 1 }}
                            >
                                {getCustomerOrderNumber(order)}
                            </Typography>
                        </Box>
                        <Chip
                            icon={ready ? <CheckCircle /> : <AccessTime />}
                            label={
                                ready
                                    ? t("kioskPreview.ready")
                                    : t("kioskPreview.preparing")
                            }
                            color={ready ? "success" : "warning"}
                            sx={{ fontWeight: 900, fontSize: 14, height: 28 }}
                        />
                    </Box>

                    {order.customer_name && (
                        <Typography variant="body1" noWrap title={order.customer_name} sx={{ fontWeight: 800 }}>
                            {order.customer_name}
                        </Typography>
                    )}

                    <Box sx={{ mt: "auto !important" }}>
                        {ready ? (
                            <Typography variant="body1" sx={{ fontWeight: 900 }}>
                                {t("kioskPreview.collectOrder")}
                            </Typography>
                        ) : (
                            <Stack spacing={0.5}>
                                <Typography variant="body1" sx={{ fontWeight: 800 }}>
                                    {t("kioskPreview.expectedAt", {
                                        time: expectedLabel,
                                    })}
                                </Typography>
                                {remainingMinutes !== null && (
                                    <Typography variant="body2">
                                        {t("kioskPreview.minutesRemaining", {
                                            count: remainingMinutes,
                                        })}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </Box>

                    {ready && (
                        <Button
                            fullWidth
                            size="large"
                            variant="contained"
                            color="success"
                            disabled={isDelivering}
                            onClick={() => onDelivered(order)}
                            sx={{ py: 0.5, minHeight: 36, fontWeight: 900, fontSize: 14 }}
                        >
                            {isDelivering
                                ? t("kioskPreview.removing")
                                : t("kioskPreview.delivered")}
                        </Button>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default function KioskPreview() {
    const { t } = useTranslation();
    const { socket, company, posProfile } = useContext(AppContext);
    const [now, setNow] = useState(Date.now());
    const snapshotRef = useRef(null);
    const chimeRef = useRef(null);
    const [soundEnabled, setSoundEnabled] = useState(false);
    const enableSound = async () => {
        chimeRef.current = chimeRef.current || createOrderChime();
        const enabled = await chimeRef.current.play();
        setSoundEnabled(enabled);
        if (!enabled) enqueueSnackbar(t("kioskPreview.soundBlocked"), { variant: "warning" });
    };
    useEffect(() => () => chimeRef.current?.close(), []);
    useEffect(() => { snapshotRef.current = null; }, [company?.name, posProfile?.name]);
    const filters = useMemo(() => {
        const result = fulfillmentFilters();
        if (company?.name) result.push(["company", "=", company.name]);
        if (posProfile?.name) result.push(["pos_profile", "=", posProfile.name]);
        return result;
    }, [company?.name, posProfile?.name]);

    const {
        data: fetchedOrders,
        isLoading,
        isError,
        refetch,
    } = useGetDocTypeListWithChildren({
        doctype:
            company?.name && posProfile?.name ? "Table Order" : null,
        filters,
        order_by: "creation asc",
    });

    const orders = useMemo(
        () => (fetchedOrders || []).filter((order) => !isOrderDelivered(order)),
        [fetchedOrders]
    );
    const preparingOrders = useMemo(
        () => orders.filter((order) => !isOrderReady(order)),
        [orders]
    );
    const readyOrders = useMemo(() => orders.filter(isOrderReady), [orders]);

    const deliveredMutation = useMutation(async (order) => {
        const { data } = await axios.post(
            "method/restaurant_management.api.mark_kiosk_order_delivered",
            { order_name: order.name }
        );
        return data?.message;
    });

    const handleDelivered = (order) => {
        deliveredMutation.mutate(order, {
            onSuccess: () => refetch(),
            onError: () =>
                enqueueSnackbar(t("kioskPreview.deliveryError"), {
                    variant: "error",
                }),
        });
    };

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 30000);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!fetchedOrders || isLoading || isError) return;
        const current = orderSnapshot(orders);
        if (soundEnabled && hasOrderNotification(snapshotRef.current, current)) {
            chimeRef.current?.play().then(ok => { if (!ok) setSoundEnabled(false); });
        }
        snapshotRef.current = current;
    }, [orders, fetchedOrders, isLoading, isError, soundEnabled]);

    useEffect(() => {
        const poll = window.setInterval(refetch, 10000);
        if (!socket?.on) return () => window.clearInterval(poll);

        const refreshOrders = (event) => {
            if (!event?.doctype || event.doctype === "Table Order") refetch();
        };
        socket.emit("doctype_subscribe", "Table Order");
        socket.on("list_update", refreshOrders);
        socket.on("kiosk_order_delivered", refreshOrders);
        return () => {
            window.clearInterval(poll);
            socket.off?.("list_update", refreshOrders);
            socket.off?.("kiosk_order_delivered", refreshOrders);
            socket.emit("doctype_unsubscribe", "Table Order");
        };
    }, [socket, refetch]);

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f5f6f8", py: 1.5 }}>
            <HelmetMeta title={t("kioskPreview.helmetTitle")} />
            <Container maxWidth="xl">
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 1.5,
                    }}
                >
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 950 }}>
                            {t("kioskPreview.title")}
                        </Typography>
                        <Typography color="text.secondary">
                            {t("kioskPreview.subtitle")}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <LanguageToggle />
                        <Button variant="outlined" onClick={enableSound} startIcon={<NotificationsActive />}>
                            {t(soundEnabled ? "kioskPreview.testSound" : "kioskPreview.enableSound")}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={() => refetch()}
                        >
                            {t("common.refresh")}
                        </Button>
                        <LogoutButton />
                    </Stack>
                </Box>

                {!posProfile?.name ? (
                    <Alert severity="warning">
                        {t("kioskPreview.configurationError")}
                    </Alert>
                ) : isLoading ? (
                    <Box sx={{ display: "grid", placeItems: "center", py: 12 }}>
                        <CircularProgress />
                    </Box>
                ) : isError ? (
                    <Alert severity="error">{t("kioskPreview.loadError")}</Alert>
                ) : orders.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 14 }}>
                        <NotificationsActive sx={{ fontSize: 72, color: "text.disabled" }} />
                        <Typography variant="h4" sx={{ mt: 2, fontWeight: 900 }}>
                            {t("kioskPreview.noOrders")}
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                            <Chip color="success" label={`${t("kioskPreview.readyOrders")} · ${readyOrders.length}`} />
                            <Chip color="warning" label={`${t("kioskPreview.preparingOrders")} · ${preparingOrders.length}`} />
                        </Stack>
                        <Grid container spacing={1.5}>
                            {[...readyOrders, ...preparingOrders].map(order => (
                                <Grid item xs={12} sm={6} md={4} key={order.name}>
                                    <OrderStatusCard order={order} ready={isOrderReady(order)} now={now} t={t}
                                        onDelivered={handleDelivered}
                                        isDelivering={deliveredMutation.isLoading && deliveredMutation.variables?.name === order.name} />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Container>
        </Box>
    );
}
